import type { Distance, SpatialRectangle } from "@pdfking/math"
import { Floor, Scale, Subtract, Total } from "@pdfking/math"
import { ZERO_DISTANCE } from "@pdfking/math/constants"
import type { PageShape } from "@pdfking/renderer-api"

import { PALETTE } from "#constants"
import type { AgendaDesign, ContentLabels } from "#internal"

import { MonthName } from "../calendar/MonthName"
import { PlotLine } from "../layout/PlotLine"
import { PlotNotebookBackground } from "../layout/PlotNotebookBackground"
import { Slice } from "../layout/Slice"
import { PlainText } from "../text/PlainText"
import { NotebookBackground } from "../types/NotebookBackground"
import { AgendaDate } from "./AgendaDate"
import { AgendaWeeks } from "./AgendaWeeks"
import { DrawAgendaBlock } from "./DrawAgendaBlock"
import { DrawAgendaDay } from "./DrawAgendaDay"

/** The `days` of week `week` of the agenda of `year`, Monday's 0, within
 * `frame`, `pitch` their lines apart, under the week's months and its number
 * when it has a `header`: `slots` equal shares of it, side by side in
 * `columns` or one under the other, a day's block each and the ones the days
 * leave a block of notes, its months and weekdays named in `language`, its fixed words
 * `labels`. */
export const DrawAgendaWeek = ({
  design: { badge, columns, dated, hours },
  days,
  frame,
  header: headed,
  labels,
  language,
  pitch,
  slots = days.length,
  week,
  year,
}: {
  design: AgendaDesign
  days: number[]
  frame: SpatialRectangle<Distance>
  header: boolean
  labels: ContentLabels
  language: string
  pitch: Distance
  slots?: number
  week: number
  year: number
}): PageShape[] => {
  const { first } = AgendaWeeks(year)
  const date = (weekday: number) => AgendaDate(year, first + 7 * week + weekday)
  // Its days under the header, side by side or one under the other
  const [, body] = Slice.top(frame, Scale(pitch, headed ? 2 : 0))
  // One under the other each whole lines tall, from the header's rule down
  // on one ruling, each's foot the next's top
  const tall = Scale(pitch, slots * Floor(body.height / Scale(pitch, slots)))
  const boxes = columns
    ? Slice.grid(body, slots, 1, Scale(pitch, 0.5))
    : Slice.grid(
        { ...body, height: tall, y: Subtract(Total(body.y, body.height), tall) },
        1,
        slots,
        ZERO_DISTANCE
      )
  const top = Total(frame.y, frame.height)
  const font = Scale(pitch, 0.6)
  // The undated header's rule, the first day's top line, its labels on it
  const rule = Subtract(top, Scale(pitch, 2))
  const label = Scale(font, 0.7)
  const under = Total(rule, Scale(label, 0.5))
  const gutter = Scale(label, 0.6)
  const week_x = Total(frame.x, Scale(frame.width, 0.8))
  // The week's months, its first day's and its last's when they differ,
  // and its number, as its Thursday's year counts them
  const months = [...new Set([date(0).month, date(6).month])].map(m =>
    MonthName(m, language).toUpperCase()
  )
  const thursday = date(3)
  const number =
    1 +
    Floor(
      (Date.UTC(thursday.year, thursday.month - 1, thursday.day) - Date.UTC(thursday.year, 0, 1)) /
        604800000
    )
  return [
    // Its months and its number over one rule, the number's past a stroke a
    // fifth from the right; undated both left for the owner
    ...(headed
      ? [
          PlainText(
            dated ? months.join(" / ") : labels.MONTH(),
            frame.x,
            frame.width,
            under,
            label,
            dated ? PALETTE.primary : PALETTE.text
          ),
          // the number named, else nobody knows what it counts
          PlainText(
            dated ? `${labels.WEEK()} ${number}` : labels.WEEK(),
            week_x,
            Scale(frame.width, 0.2),
            under,
            label,
            PALETTE.text
          ),
          PlotLine(frame.x, rule, frame.width, ZERO_DISTANCE),
          PlotLine(Subtract(week_x, gutter), rule, ZERO_DISTANCE, Subtract(top, rule)),
        ]
      : []),
    ...days.flatMap((weekday, i) =>
      DrawAgendaDay({
        badge,
        box: boxes[i]!,
        date: dated ? date(weekday) : undefined,
        hours,
        language,
        // a column too narrow for the month beside the name
        month: !columns,
        pitch,
        ruling: undefined,
        weekday,
      })
    ),
    // The rest notes: a day's block by no hours, NOTES its name; by the hours
    // their heading where a day's name is, lines under it
    ...boxes.slice(days.length).flatMap(box => {
      if (!hours)
        return DrawAgendaBlock({
          badge,
          box,
          color: PALETTE.text,
          label: labels.NOTES(),
          number: undefined,
          pitch,
        })
      const header = Scale(pitch, 1.5)
      const note = Scale(pitch, 0.4)
      return [
        PlainText(
          labels.NOTES(),
          box.x,
          box.width,
          Subtract(Total(box.y, box.height), Scale(Total(header, note), 0.5)),
          note,
          PALETTE.primary
        ),
        ...PlotNotebookBackground({
          background: NotebookBackground.LINES,
          box: Slice.top(box, header)[1],
          pitch,
        }),
      ]
    }),
    // What the whole lines leave under them a dotted grid, clear of the last
    ...(!columns && Subtract(body.height, tall) > pitch
      ? PlotNotebookBackground({
          background: NotebookBackground.DOTS,
          box: { ...body, height: Subtract(body.height, Total(tall, Scale(pitch, 0.5))) },
          pitch,
        })
      : []),
  ]
}
